import { twMerge } from 'tailwind-merge'
import {
  CHART_HEIGHT,
  METRIC_HISTORY_LENGTH,
  SEVERITY_TEXT,
  severityFor,
  type MetricChartConfig,
} from '@renderer/features/performance-monitor/model'

interface MetricProps {
  value: number
  thresholds: readonly [number, number, number]
  digits?: number
  className?: string
}

export function Metric({ value, thresholds, digits = 1, className }: MetricProps) {
  return (
    <span className={twMerge('tabular-nums', SEVERITY_TEXT[severityFor(value, thresholds)], className)}>
      {value.toFixed(digits)}
    </span>
  )
}

interface MetricTrendChartProps {
  config: MetricChartConfig
  currentValue: number
  values: Array<number>
}

export function MetricTrendChart({ config, currentValue, values }: MetricTrendChartProps) {
  const chartValues = values.length === 1 ? [values[0], values[0]] : values
  const max = Math.max(...chartValues, 1)
  const points = chartValues.map((value, index) => ({
    x: (index / Math.max(chartValues.length - 1, 1)) * 100,
    y: CHART_HEIGHT - (value / max) * (CHART_HEIGHT - 2),
  }))
  const linePath = points.map(({ x, y }, index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ')
  const areaPath = points.length > 0 ? `${linePath} L 100 ${CHART_HEIGHT} L 0 ${CHART_HEIGHT} Z` : ''

  return (
    <div className="">
      <div className="flex items-baseline justify-between px-2.5 py-1.5">
        <span className="text-foreground-placeholder">{config.label}</span>
        <span>
          <Metric value={currentValue} thresholds={config.thresholds} digits={config.digits} />
          <span className="text-foreground-placeholder ml-0.5">{config.unit}</span>
        </span>
      </div>
      <svg
        viewBox={`0 0 100 ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        className={twMerge('mt-0.5 h-10 w-full overflow-visible', config.color)}
        role="img"
        aria-label={`${config.label} trend over the last ${METRIC_HISTORY_LENGTH} seconds`}
      >
        <path
          d={`M 0 ${CHART_HEIGHT / 2} H 100`}
          stroke="currentColor"
          strokeOpacity="0.12"
          vectorEffect="non-scaling-stroke"
        />
        <path d={areaPath} fill="currentColor" fillOpacity="0.18" />
        <path
          d={linePath}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  )
}
